import { useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { useAttachment } from "../api/use-chat";
import { privateMediaUrl } from "../lib/messages";
import type { Attachment } from "../types";
import { cn } from "@/shared/lib/utils";
import { HOVER_MUTED, HOVER_MUTED_TEXT } from "./styles";

const MEDIA_CLASS = "h-[45px] w-14 shrink-0 object-contain";

export function AttachmentDraft({
  chatId,
  attachment,
  onReady,
  onRemove,
}: {
  chatId: string;
  attachment: Attachment;
  onReady: (attachment: Attachment) => void;
  onRemove: () => void;
}) {
  const query = useAttachment(chatId, attachment.attachId);
  const current = query.data?.attachment ?? attachment;
  useEffect(() => {
    if (current.status !== attachment.status) onReady(current);
  }, [current, attachment.status, onReady]);
  const url = privateMediaUrl(current.url);
  return (
    <Card className="flex-row items-center gap-2 rounded-lg p-1.5 text-xs shadow-none">
      {current.status === "CLEAR" &&
        url &&
        (current.type === "IMAGE" ? (
          <img className={MEDIA_CLASS} src={url} alt={current.name} />
        ) : (
          <video
            className={MEDIA_CLASS}
            playsInline
            src={url}
            controls
            aria-label={current.name}
          />
        ))}
      <span className="min-w-0 flex-1 wrap-anywhere">
        {current.name}
        <small className="block text-muted-foreground">
          {query.error
            ? "Status unavailable"
            : current.status === "CLEAR"
              ? "Ready to send"
              : current.status === "BLOCKED"
                ? "Processing failed. Remove and upload again."
                : "Processing media…"}
        </small>
      </span>
      {query.error && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn("min-h-11 cursor-pointer px-2 text-xs", HOVER_MUTED)}
          onClick={() => void query.refetch()}
        >
          Retry status
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          "size-11 min-h-11 min-w-11 shrink-0 cursor-pointer rounded-full text-muted-foreground",
          HOVER_MUTED_TEXT,
        )}
        aria-label={`Remove attachment ${current.name}`}
        onClick={onRemove}
      >
        <X className="size-3.5" />
      </Button>
    </Card>
  );
}
